// Per-scorecard section/criteria pass-rate breakdown. Hand-rolled
// hierarchical list (sections → criteria) so the visual density,
// padding, expand/collapse animations, and tier-colored bars match
// the rest of the dashboard rather than inheriting the prod
// AdvancedTable chrome.
//
// Data comes from the bundled `fetchScorecardOverview` action (one
// round trip shared with CriteriaTrends + CriteriaCorrelation).
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { fetchScorecardOverview } from '@/reducers/qa-copilot/qa-copilot.actions'

import { EntityClickContext } from '../helpers/dashboardContexts'
import { colorForPassRate } from '../helpers/format'
import { ShowCallsButton } from '../components/ShowCallsButton'
import { useClassicScorecardSelection } from '../hooks/useClassicScorecardSelection'

// 4-tier classification keyed off pass rate (0..100). Drives the
// "exceeds / meets / improvement / critical" SCSS classes that
// color the pill, bar, and inline value the same way.
const tierClassForPassRate = (pct) => {
  if (pct >= 90) {
    return 'scorecard-perf-tier-exceeds'
  }
  if (pct >= 80) {
    return 'scorecard-perf-tier-meets'
  }
  if (pct >= 70) {
    return 'scorecard-perf-tier-improvement'
  }
  return 'scorecard-perf-tier-critical'
}

const formatPct = (rate0to1) =>
  isNil(rate0to1) ? '—' : `${(parseFloat(rate0to1) * 100).toFixed(1)}%`

export const ScorecardPerformanceTable = () => {
  const dispatch = useDispatch()
  const clickEntity = useContext(EntityClickContext)
  const {
    loading,
    data: { scorecardPerformance },
    filters,
  } = useSelector((state) => state.qaCopilot)

  // Source of truth: the scope picker. Falls back to the first
  // available scorecard so the widget renders something on first
  // mount before the picker auto-selects.
  const { selectedScorecard, selectedScorecardOption, scorecardSelect } =
    useClassicScorecardSelection('scorecard-perf-select')

  useEffect(() => {
    if (selectedScorecard) {
      dispatch(fetchScorecardOverview(selectedScorecard))
    }
  }, [selectedScorecard, filters, dispatch])

  // Sections (worst first), each carrying its non-null criteria.
  const sections = useMemo(() => {
    if (loading.scorecardPerformance || isEmpty(scorecardPerformance)) {
      return []
    }
    return (scorecardPerformance.sections || [])
      .filter((section) => !isNil(section.pass_rate))
      .map((section) => ({
        ...section,
        criteria: (section.criteria || []).filter((criterion) => !isNil(criterion.pass_rate)),
      }))
      .sort((sectionA, sectionB) => sectionA.pass_rate - sectionB.pass_rate)
  }, [scorecardPerformance, loading.scorecardPerformance])

  // Expand-all by default so coaches see every criterion without an
  // extra click. Per-section toggling overrides this; clicking
  // "Collapse all" empties the set; "Expand all" repopulates it.
  const [expandedIds, setExpandedIds] = useState([])
  useEffect(() => {
    setExpandedIds(sections.map((section) => section.id))
  }, [sections])

  const anyCollapsed = sections.some((section) => !expandedIds.includes(section.id))
  const toggleSection = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((expandedId) => expandedId !== id) : [...prev, id]
    )
  }
  const toggleAll = () => {
    if (anyCollapsed) {
      setExpandedIds(sections.map((section) => section.id))
    } else {
      setExpandedIds([])
    }
  }

  const handleCriterionClick = (criterion) => {
    // The scorecard-overview bundle keys each criterion by `id` (the
    // scid); other feeds use `criteria_id`. Accept either, matching what
    // CriterionDetailDrawer already resolves — without the `id` fallback
    // every click here silently no-oped.
    const criteriaId = criterion?.criteria_id || criterion?.id
    if (!clickEntity || !criteriaId) {
      return
    }
    clickEntity({
      type: 'criterion',
      payload: {
        criteria_id: criteriaId,
        name: criterion.name || criterion.criteria_name,
        scorecard_config_sid: selectedScorecard,
      },
    })
  }

  const headlinePct =
    !isEmpty(scorecardPerformance) && !isNil(scorecardPerformance.pass_rate)
      ? scorecardPerformance.pass_rate * 100
      : null

  const cardTitle = (
    <>
      Scorecard Performance
      {selectedScorecardOption?.label && (
        <span className="card-subtitle">· {selectedScorecardOption.label}</span>
      )}
    </>
  )

  if (!selectedScorecard) {
    return (
      <Card title="Scorecard Performance">
        <div className="scorecard-perf-empty">
          No scorecards available. Pick one in the filter bar above.
        </div>
      </Card>
    )
  }

  return (
    <Card
      title={cardTitle}
      info="Each section and criterion's pass rate for the selected scorecard, worst first. Click a criterion to see the agents and calls failing it."
      buttons={scorecardSelect}
      className="no-padding scorecard-perf-card"
    >
      <div className="scorecard-perf-header">
        <div>
          <div className="scorecard-perf-headline-label">Average Score</div>
          <div
            className={classNames(
              'scorecard-perf-headline',
              !isNil(headlinePct) && tierClassForPassRate(headlinePct)
            )}
          >
            {isNil(headlinePct) ? '—' : `${headlinePct.toFixed(1)}%`}
          </div>
        </div>
        {!isEmpty(sections) && (
          <div className="scorecard-perf-actions">
            <ShowCallsButton
              label="Failing calls"
              title="Open the call browser scoped to this scorecard's below-threshold calls"
              scope={selectedScorecard ? { scorecards: [{ value: selectedScorecard }] } : undefined}
              tier="below"
            />
            <Button basic size="small" onClick={toggleAll}>
              {anyCollapsed ? 'Expand all' : 'Collapse all'}
            </Button>
          </div>
        )}
      </div>

      {loading.scorecardPerformance && <div className="scorecard-perf-empty">Loading…</div>}
      {!loading.scorecardPerformance && isEmpty(sections) && (
        <div className="scorecard-perf-empty">No section data for this scorecard.</div>
      )}
      {!loading.scorecardPerformance && !isEmpty(sections) && (
        <ul className="scorecard-perf-sections">
          {sections.map((section) => {
            const sectionPct = section.pass_rate * 100
            const tier = tierClassForPassRate(sectionPct)
            const isExpanded = expandedIds.includes(section.id)
            const Chevron = isExpanded ? IconChevronDown : IconChevronRight
            return (
              <li key={section.id} className="scorecard-perf-section">
                <button
                  type="button"
                  className="scorecard-perf-section-header"
                  aria-expanded={isExpanded}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="scorecard-perf-section-toggle">
                    <Chevron size={16} />
                  </span>
                  <span className="scorecard-perf-section-name">{section.name}</span>
                  {/* Stays a styled span, not a shared Pill: this sits inside the
                      disclosure <button>, which may only contain phrasing content —
                      a Pill's <div> would make the button markup invalid. */}
                  <span className={classNames('scorecard-perf-pass-pill', tier)}>
                    {formatPct(section.pass_rate)}
                  </span>
                </button>
                {isExpanded && !isEmpty(section.criteria) && (
                  <ul className="scorecard-perf-criteria">
                    {section.criteria
                      .slice()
                      .sort((criterionA, criterionB) => criterionA.pass_rate - criterionB.pass_rate)
                      .map((criterion) => {
                        const cPct = criterion.pass_rate * 100
                        const cTier = tierClassForPassRate(cPct)
                        return (
                          <li
                            key={criterion.criteria_id || criterion.id}
                            className="scorecard-perf-criterion"
                            title="View failing agents and calls for this criterion"
                            onClick={() => handleCriterionClick(criterion)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                handleCriterionClick(criterion)
                              }
                            }}
                            role="button" // eslint-disable-line jsx-a11y/no-noninteractive-element-to-interactive-role
                            tabIndex={0}
                          >
                            <span className="scorecard-perf-criterion-name">
                              {criterion.name || criterion.criteria_name}
                            </span>
                            <span className="scorecard-perf-bar-wrap">
                              <span
                                className={classNames('scorecard-perf-bar', cTier)}
                                style={{
                                  width: `${Math.max(2, Math.min(100, cPct))}%`,
                                  background: colorForPassRate(cPct),
                                }}
                              />
                            </span>
                            <span className={classNames('scorecard-perf-pass-value', cTier)}>
                              {formatPct(criterion.pass_rate)}
                            </span>
                          </li>
                        )
                      })}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
